import { CalculateCurrentCardStyled } from "../CotermWorkStyled";
import { LuCalculator, LuTv } from "react-icons/lu";
import {
  CALCULATE_TYPE,
  CotermCalculateType,
} from "../SubscriptonCotermWorkModal";
import InputDate from "components/atoms/calendar/InputDate";
import { InputNumber } from "antd";
import { useAddChannelCalculator } from "../useAddChannelCalculator";
import { usePurchaseCalculator } from "../usePurchaseCalculator";
import { durationOptions } from "utils/options";
import { TbPlusMinus } from "react-icons/tb";
import { AiOutlineShoppingCart } from "react-icons/ai";
import { useMemo } from "react";
import { normalizeToStartOfDay } from "utils/timeUtil";
import SelectComponent from "components/atoms/select/SelectComponent";

type Props = {
  tabType: CotermCalculateType;
  addChannelCalc: ReturnType<typeof useAddChannelCalculator>;
  purchaseCalc: ReturnType<typeof usePurchaseCalculator>;
  expiration: string;
};

export default function CurrentCard({
  tabType,
  addChannelCalc,
  purchaseCalc,
  expiration,
}: Props): JSX.Element {
  const { inputs: channelInputs, onChangeAddChannel } = addChannelCalc;
  const { inputs: purchaseInputs, onChangePurchase } = purchaseCalc;

  const safeRegisteredChannels = Number(channelInputs.registeredChannel) || 0;

  const safeAddChannels = Number(channelInputs.addChannels) || 0;

  // Constraint: registeredChannel + addChannels >= 1 (CHANNEL tab only)
  const minRegisteredChannels = Math.max(1, 1 - safeAddChannels);
  const minAddChannels = 1 - safeRegisteredChannels;

  const expirationDate = useMemo(() => {
    if (typeof channelInputs.expirationDate === "string") {
      return channelInputs.expirationDate;
    }
    return channelInputs.expirationDate?.toISOString() ?? "";
  }, [channelInputs.expirationDate]);

  const purchaseExpirationMinDate = useMemo(() => {
    const parsed = expiration ? new Date(expiration) : new Date();
    const safeBase = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
    return normalizeToStartOfDay(safeBase);
  }, [expiration]);

  return (
    <CalculateCurrentCardStyled.Card>
      <div className="title">
        <h2>
          <LuCalculator size={18} className="icon" />
          Current Subscription
        </h2>
      </div>
      {tabType === CALCULATE_TYPE.CHANNEL && (
        <ul className="items">
          <li>
            <div className="item-title">Registered Channels</div>
            <InputNumber
              value={channelInputs.registeredChannel}
              onChange={(value) =>
                onChangeAddChannel({ name: "registeredChannel", value })
              }
              min={minRegisteredChannels}
            />
          </li>

          <li>
            <h2>
              <TbPlusMinus />
              Add / Reduce Channels
            </h2>
            <InputNumber
              value={channelInputs.addChannels}
              onChange={(value) =>
                onChangeAddChannel({ name: "addChannels", value })
              }
              min={minAddChannels}
            />
            <span className="desc">
              Use positive numbers to add, negative to reduce
            </span>
          </li>

          <li>
            <div className="item-title">Expiration Date</div>
            <InputDate
              value={expirationDate}
              timezone="UTC"
              readOnly
              disabled
            />
          </li>
        </ul>
      )}

      {tabType === CALCULATE_TYPE.PURCHASE && (
        <ul className="items">
          <li>
            <div className="item-title">Number of Registered Channels</div>
            <InputNumber
              value={purchaseInputs.activatedChannels}
              onChange={(value) =>
                onChangePurchase({ name: "activatedChannels", value })
              }
              min={1}
            />
          </li>
          <li>
            <div className="item-title">Current Expiration Date</div>
            <InputDate
              value={String(purchaseInputs.expirationDate)}
              onChange={(value) =>
                onChangePurchase({ name: "expirationDate", value })
              }
              timezone="UTC"
              minDate={purchaseExpirationMinDate}
            />
          </li>
          <h2>
            <AiOutlineShoppingCart size={18} />
            Purchase Subscription
          </h2>
          <li>
            <div className="item-title">Duration</div>
            <SelectComponent
              placeholder="Select duration"
              options={durationOptions}
              value={durationOptions.find(
                (opt) => opt.value === String(purchaseInputs.duration)
              )?.value}
              onChange={(value: string) =>
                onChangePurchase({
                  name: "duration",
                  value: value,
                })
              }
            />
          </li>
          <li>
            <div className="item-title">Number of Channels to Purchase</div>
            <InputNumber
              value={purchaseInputs.addChannels}
              onChange={(value) =>
                onChangePurchase({ name: "addChannels", value })
              }
              min={0}
            />
          </li>
        </ul>
      )}
    </CalculateCurrentCardStyled.Card>
  );
}
